import { apiFetch, apiFetchEnvelope, type ListMeta } from "@core/http";
import type { CatalogEntry, Profile, User, UserInput } from "@core/types";

// `UserCreateInput` (openapi.d.ts) se genera como
// `UserInput & Record<string, never>`: la intersección con un índice
// `never` colapsa TODAS las propiedades heredadas de UserInput a `never`,
// así que ningún objeto real es asignable a ese tipo (verificado con `tsc`
// directo — TS2322 en cada propiedad). Es una limitación conocida de
// openapi-typescript con `allOf` + `required` sin propiedades nuevas, no
// una forma de uso distinta: se declara el alias localmente, con la misma
// forma que documenta el contrato (`UserInput` con `username` obligatorio).
export type UserCreateInput = UserInput & { username: string };

const base = "/v1/portal/security/users";

export type UserActiveFilter = "all" | "active" | "inactive";

export interface UsersQuery {
  search: string;
  profileId: string;
  active: UserActiveFilter;
  cursor?: string | undefined;
}

export interface UsersPage {
  items: User[];
  meta: ListMeta;
}

function usersSearchParams(query: UsersQuery) {
  const params = new URLSearchParams();
  if (query.search.trim()) params.set("search", query.search.trim());
  if (query.profileId) params.set("profileId", query.profileId);
  if (query.active !== "all")
    params.set("active", query.active === "active" ? "true" : "false");
  if (query.cursor) params.set("cursor", query.cursor);
  return params;
}

export async function listUsers(query: UsersQuery): Promise<UsersPage> {
  const params = usersSearchParams(query);
  const suffix = params.size ? `?${params}` : "";
  const envelope = await apiFetchEnvelope<User[]>(`${base}${suffix}`);
  return {
    items: envelope.data,
    meta: envelope.meta ?? { next: null, total: envelope.data.length },
  };
}

export const getUser = (id: string) =>
  apiFetch<User>(`${base}/${encodeURIComponent(id)}`);

export const createUser = (input: UserCreateInput) =>
  apiFetch<User>(base, { method: "POST", body: input });

export const editUser = (id: string, input: UserInput) =>
  apiFetch<User>(`${base}/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: input,
  });

export const inactivateUser = (id: string) =>
  apiFetch<void>(`${base}/${encodeURIComponent(id)}`, { method: "DELETE" });

export const reactivateUser = (id: string) =>
  apiFetch<void>(`${base}/${encodeURIComponent(id)}/activate`, {
    method: "PUT",
  });

export interface ResetPasswordResult {
  maskedEmail?: string;
  validityHours?: number;
}

export const resetUserPassword = (id: string) =>
  apiFetch<ResetPasswordResult>(
    `${base}/${encodeURIComponent(id)}/reset-password`,
    { method: "POST" },
  );

export const listProfiles = () =>
  apiFetch<Profile[]>("/v1/portal/security/profiles");

// El lugar de un usuario de validador es un comercio aliado o un taller de
// Teojama (ANEXO-011) — dos de los quince catálogos administrables (SPEC-011),
// consultados aquí solo entre los activos (default `includeInactive=false`)
// para poblar el selector de lugar del formulario.
export type PlaceCatalog = "merchants" | "workshops";

export const listActivePlaces = (catalog: PlaceCatalog) =>
  apiFetch<CatalogEntry[]>(`/v1/portal/catalogs/${catalog}/entries`);
